import { BANK_PROBLEM, type BankConnection, type ConnectBankBody } from '@klopotun/contracts';
import { useState } from 'react';
import {
  useBankStateQuery,
  useConnectBankMutation,
  useDeleteBankDataMutation,
  useDisconnectBankMutation,
  useUpdateBankAccountMutation,
} from '../../../entities/bank/index.js';
import { useCardsQuery } from '../../../entities/card/index.js';
import { BankConnectForm } from '../../../features/bank-connect-form/index.js';
import { Button, ErrorState, Sheet, Spinner, type SheetAction } from '../../../shared/ui/index.js';
import { BankAccountRow } from './bank-account-row/bank-account-row.js';
import styles from './bank-section.module.css';

type BankConfirmation = 'disconnect' | 'delete' | null;

const isTokenRejected = (connection: BankConnection): boolean =>
  connection.problem === BANK_PROBLEM.TOKEN_REJECTED;

const statusOf = (connection: BankConnection): string => {
  const connectedOn = new Date(connection.connectedAt).toLocaleDateString('uk-UA');

  if (!connection.isConnected) {
    return "Від'єднано: токен видалено, рахунки й картки лишились";
  }

  if (isTokenRejected(connection)) {
    return 'Банк більше не приймає токен — підключіть його знову';
  }

  return `${connection.clientName} · підключено ${connectedOn}`;
};

export const BankSection = () => {
  const bank = useBankStateQuery();
  const cards = useCardsQuery({ includeArchived: true });
  const connectBank = useConnectBankMutation();
  const disconnectBank = useDisconnectBankMutation();
  const deleteBankData = useDeleteBankDataMutation();
  const updateAccount = useUpdateBankAccountMutation();
  const [confirmation, setConfirmation] = useState<BankConfirmation>(null);

  const connect = (body: ConnectBankBody): void => {
    connectBank.mutate(body);
  };

  const closeConfirmation = (): void => {
    setConfirmation(null);
  };

  if (bank.isError) {
    return (
      <ErrorState
        title="Не вдалося завантажити банк"
        message={bank.error.message}
        onRetry={() => {
          void bank.refetch();
        }}
      />
    );
  }

  if (bank.isPending) {
    return (
      <div className={styles.centered}>
        <Spinner label="Завантаження" />
      </div>
    );
  }

  const { connection } = bank.data;

  if (connection === null) {
    return (
      <div className={styles.section}>
        <h2 className={styles.subtitle}>Підключити monobank</h2>
        <BankConnectForm
          busy={connectBank.isPending}
          errorMessage={connectBank.error?.message ?? null}
          submitLabel="Підключити"
          onSubmit={connect}
        />
      </div>
    );
  }

  const disconnectActions: SheetAction[] = [
    {
      id: 'confirm-disconnect',
      label: "Так, від'єднати",
      disabled: disconnectBank.isPending,
      onSelect: () => {
        disconnectBank.mutate(undefined, { onSettled: closeConfirmation });
      },
    },
  ];

  const deleteActions: SheetAction[] = [
    {
      id: 'confirm-delete',
      label: 'Так, видалити',
      danger: true,
      disabled: deleteBankData.isPending,
      onSelect: () => {
        deleteBankData.mutate(undefined, { onSettled: closeConfirmation });
      },
    },
  ];

  const actionError = disconnectBank.error ?? deleteBankData.error ?? updateAccount.error;

  return (
    <div className={styles.section}>
      <div className={styles.heading}>
        <h2 className={styles.subtitle}>monobank</h2>
        <p className={styles.hint}>{statusOf(connection)}</p>
      </div>

      {actionError !== null && (
        <p className={styles.error} role="alert">
          {actionError.message}
        </p>
      )}

      {connection.isConnected && !isTokenRejected(connection) ? (
        <>
          <div className={styles.accounts}>
            {connection.accounts.map((account) => (
              <BankAccountRow
                key={account.id}
                account={account}
                cards={cards.data ?? []}
                busy={updateAccount.isPending}
                onChange={(body) => {
                  updateAccount.mutate({ id: account.id, body });
                }}
              />
            ))}
          </div>
          <Button
            variant="secondary"
            onClick={() => {
              setConfirmation('disconnect');
            }}
          >
            Від'єднати банк
          </Button>
        </>
      ) : (
        <BankConnectForm
          busy={connectBank.isPending}
          errorMessage={connectBank.error?.message ?? null}
          submitLabel="Підключити знову"
          onSubmit={connect}
        />
      )}

      <Button
        variant="danger"
        onClick={() => {
          setConfirmation('delete');
        }}
      >
        Видалити всі дані банку
      </Button>

      <Sheet
        open={confirmation === 'disconnect'}
        actions={disconnectActions}
        title="Від'єднати банк? Токен буде видалено, рахунки й картки лишаться."
        closeLabel="Ні, лишити"
        onClose={closeConfirmation}
      />

      <Sheet
        open={confirmation === 'delete'}
        actions={deleteActions}
        title="Видалити всі дані банку? Зникнуть підключення й рахунки; картки й підписки лишаться."
        closeLabel="Ні, лишити"
        onClose={closeConfirmation}
      />
    </div>
  );
};
